import { useMemo, useState } from "react";
import { toast } from "sonner";
import SEOHead from "@/components/SEOHead";
import { postCommission } from "@/lib/api";
import { TID } from "@/constants/testIds";

const PORTRAIT_TYPES = [
  { id: "single_subject", label: "Single subject" },
  { id: "dual_ambidextrous", label: "Dual — one subject per hand" },
  { id: "couple", label: "Couple / partners" },
  { id: "family", label: "Family study" },
];
const MEDIUMS = [
  { id: "ink", label: "Ink on paper" },
  { id: "charcoal", label: "Charcoal & pastel" },
  { id: "watercolor", label: "Watercolor on paper" },
  { id: "oil", label: "Oil on canvas" },
  { id: "mixed", label: "Mixed media on linen" },
  { id: "artist", label: "Let Colin decide" },
];
const SIZE_NOTES = [
  { id: "under_5ft", label: 'Under 5 ft in every dimension (flat $5,000)' },
  { id: "over_5ft", label: "Over 5 ft — larger works quoted separately" },
];

const BASE_PRICE = 5000;
const VIDEO_PRICE = 2000;

export default function Commission() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    portrait_type: PORTRAIT_TYPES[1].id,
    medium: MEDIUMS[5].id,
    canvas_size: SIZE_NOTES[0].id,
    subjects: "",
    notes: "",
    reference_urls: "",
    include_video: true,
  });
  const [busy, setBusy] = useState(false);

  const isOversize = form.canvas_size === "over_5ft";

  const estimate = useMemo(() => {
    if (isOversize) return null;
    return BASE_PRICE + (form.include_video ? VIDEO_PRICE : 0);
  }, [form.include_video, isOversize]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const notes =
        (form.include_video
          ? "[Creation video: YES — +$2,000]\n"
          : "[Creation video: no]\n") + (form.notes || "");
      await postCommission({ ...form, notes });
      toast.success("Commission request sent. Colin will be in touch.");
      setForm((f) => ({
        ...f,
        name: "",
        email: "",
        phone: "",
        subjects: "",
        notes: "",
        reference_urls: "",
      }));
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Commission a Custom Ambidextrous Portrait | Colin Darke"
        description="Commission a custom portrait drawn or painted with both hands simultaneously. Flat $5,000 for any commission under 5 feet, plus an optional $2,000 ambidextrous creation video."
        path="/commission"
      />

      <section className="pt-16 pb-6 border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16">
          <p className="catalog-label mb-4">Commission</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl">
            A portrait, drawn in front of you.
            <br />
            <span className="italic text-[hsl(var(--vermillion))]">
              A performance you get to keep.
            </span>
          </h1>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12">
          {/* Info column */}
          <aside className="lg:col-span-4 space-y-8">
            <div>
              <p className="catalog-label mb-3">How it works</p>
              <ol className="space-y-4 text-[hsl(var(--ink))]">
                <li className="flex gap-3">
                  <span className="font-serif text-2xl text-[hsl(var(--vermillion))] leading-none">01</span>
                  <span>Share who you'd like drawn — one subject, a couple, or a family.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif text-2xl text-[hsl(var(--vermillion))] leading-none">02</span>
                  <span>Confirm size and decide whether the creation video is part of the piece.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif text-2xl text-[hsl(var(--vermillion))] leading-none">03</span>
                  <span>Send reference photos. Colin reviews personally and follows up.</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-serif text-2xl text-[hsl(var(--vermillion))] leading-none">04</span>
                  <span>The portrait is drawn in a single two-handed session — the performance is the piece.</span>
                </li>
              </ol>
            </div>

            {/* Clean price card */}
            <div className="bg-[hsl(var(--ink))] text-[hsl(var(--bg))] p-6" data-testid="commission-price-card">
              <p className="catalog-label text-[hsl(var(--bg))]/60 mb-4">
                Commission pricing
              </p>
              <div className="space-y-4">
                <div className="flex items-baseline justify-between border-b border-[hsl(var(--bg))]/15 pb-4">
                  <div>
                    <p className="font-serif text-2xl leading-none">$5,000</p>
                    <p className="mt-2 text-xs text-[hsl(var(--bg))]/70">
                      Flat rate for any commission under 5 ft in every dimension
                    </p>
                  </div>
                </div>
                <div className="flex items-baseline justify-between">
                  <div>
                    <p className="font-serif text-2xl leading-none">
                      + $2,000
                    </p>
                    <p className="mt-2 text-xs text-[hsl(var(--bg))]/70">
                      Optional — the ambidextrous creation video
                    </p>
                  </div>
                </div>
                <div className="pt-4 mt-2 border-t border-[hsl(var(--bg))]/15">
                  <p className="catalog-label text-[hsl(var(--bg))]/60">
                    Your selection
                  </p>
                  <p
                    className="font-serif text-5xl leading-none mt-2"
                    data-testid="commission-total"
                  >
                    {estimate ? `$${estimate.toLocaleString()}` : "Quote"}
                  </p>
                  <p className="mt-2 text-xs text-[hsl(var(--bg))]/70">
                    {isOversize
                      ? "For works over 5 ft, Colin quotes on the specifics of the piece."
                      : form.include_video
                      ? "Includes framing consultation, shipping (continental U.S.) and the full ambidextrous creation video."
                      : "Includes framing consultation and shipping (continental U.S.)."}
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid={TID.commissionForm}
            className="lg:col-span-8 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Your name" required>
                <input
                  required
                  data-testid={TID.commissionName}
                  value={form.name}
                  onChange={set("name")}
                  className="input"
                />
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  data-testid={TID.commissionEmail}
                  value={form.email}
                  onChange={set("email")}
                  className="input"
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  data-testid={TID.commissionPhone}
                  value={form.phone}
                  onChange={set("phone")}
                  className="input"
                />
              </Field>
              <Field label="Portrait type" required>
                <select
                  data-testid={TID.commissionType}
                  value={form.portrait_type}
                  onChange={set("portrait_type")}
                  className="input"
                >
                  {PORTRAIT_TYPES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Preferred medium">
                <select
                  data-testid={TID.commissionMedium}
                  value={form.medium}
                  onChange={set("medium")}
                  className="input"
                >
                  {MEDIUMS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Size" required>
                <select
                  data-testid={TID.commissionSize}
                  value={form.canvas_size}
                  onChange={set("canvas_size")}
                  className="input"
                >
                  {SIZE_NOTES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {/* Video add-on */}
            <label
              className={`flex gap-4 p-5 border cursor-pointer transition-colors ${
                form.include_video
                  ? "border-[hsl(var(--vermillion))] bg-[hsl(var(--surface-elevated))]"
                  : "border-[hsl(var(--line))] hover:border-[hsl(var(--ink))]"
              }`}
              data-testid="commission-video-toggle"
            >
              <input
                type="checkbox"
                checked={form.include_video}
                onChange={toggle("include_video")}
                className="mt-1 w-4 h-4 accent-[hsl(var(--vermillion))]"
                data-testid="commission-video-checkbox"
              />
              <div className="flex-1">
                <p className="font-serif text-xl">
                  Include the ambidextrous creation video
                  <span className="ml-3 catalog-label text-[hsl(var(--vermillion))]">
                    +$2,000
                  </span>
                </p>
                <p className="text-sm text-[hsl(var(--ink-muted))] mt-1">
                  A professionally-edited film of Colin drawing your commission
                  in real time. The performance is often the part collectors
                  end up watching more than the piece itself.
                </p>
              </div>
            </label>

            <Field label="Who are we drawing?" required>
              <input
                required
                data-testid={TID.commissionSubjects}
                value={form.subjects}
                onChange={set("subjects")}
                placeholder="e.g. My partner and I — a dual ambidextrous portrait for our anniversary."
                className="input"
              />
            </Field>

            <Field label="Reference photo links (optional)">
              <input
                data-testid={TID.commissionRefs}
                value={form.reference_urls}
                onChange={set("reference_urls")}
                placeholder="Google Drive, Dropbox, iCloud share links — one per line"
                className="input"
              />
            </Field>

            <Field label="Anything else Colin should know?">
              <textarea
                data-testid={TID.commissionNotes}
                value={form.notes}
                onChange={set("notes")}
                rows={5}
                placeholder="Tone, symbolism, story behind the subjects, deadline…"
                className="input resize-y"
              />
            </Field>

            <button
              type="submit"
              disabled={busy}
              data-testid={TID.commissionSubmit}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-8 py-4 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--vermillion))] transition-colors disabled:opacity-50"
            >
              {busy
                ? "Sending…"
                : isOversize
                ? "Request a quote"
                : `Request commission · $${estimate.toLocaleString()}`}
            </button>
          </form>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          padding: 12px 14px;
          background: hsl(var(--surface));
          border: 1px solid hsl(var(--line));
          border-radius: 2px;
          font-size: 15px;
          color: hsl(var(--ink));
          transition: border-color 200ms ease;
        }
        .input:focus {
          outline: none;
          border-color: hsl(var(--ink));
        }
      `}</style>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="catalog-label mb-2 block">
        {label}
        {required && <span className="text-[hsl(var(--vermillion))]"> *</span>}
      </span>
      {children}
    </label>
  );
}
